import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import {
    loadElearningCourses,
    loadElearningModules,
} from '../elearningCache';
import {
    PROGRAM_CONFIG,
    PROGRAM_IDS,
    calcProgress,
} from '../ELearningWorkspace/programConfig';
import ProgressActivityChart from './ProgressActivityChart';
import './ProgramsProgressPanel.css';

function towardNextMilestone(value, milestones) {
    const n = Math.max(0, Number(value) || 0);
    const next =
        milestones.find((m) => m > n) ||
        Math.max(n + 1, Math.ceil((n + 1) / 100) * 100);
    const prev =
        [...milestones].reverse().find((m) => m <= n) || 0;
    if (next <= prev) return 100;
    return Math.round(((n - prev) / (next - prev)) * 100);
}

const ACTION_MILESTONES = [
    7, 15, 30, 50, 75, 100, 150, 200, 250, 300, 350, 450, 500, 750, 1000,
];

const DAILY_ACTION_TARGET = 3;

function ProgressRing({ percent = 0, size = 64, color = '#c9a227' }) {
    const stroke = 4;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    const offset = c * (1 - pct / 100);

    return (
        <div
            className="dv2-pp__ring"
            style={{ width: size, height: size, color }}
            aria-hidden
        >
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.18"
                    strokeWidth={stroke}
                />
                {pct > 0 ? (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={`${c} ${c}`}
                        strokeDashoffset={offset}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                ) : null}
            </svg>
            <span className="dv2-pp__ring-value">{pct}%</span>
        </div>
    );
}

function blueprintProgress(userStepId) {
    const step = Math.max(0, Number(userStepId) || 0);
    if (step >= 4) {
        return {
            id: 'blueprint',
            title: 'Design My Life Blueprint',
            kind: 'blueprint',
            percent: 100,
            completed: 4,
            total: 4,
            status: 'completed',
            detail: 'Blueprint complete',
            color: '#5b9fd4',
        };
    }
    const completed = Math.min(3, Math.max(0, step));
    const labels = {
        0: 'Not started',
        1: 'Oola Wheel in progress',
        2: 'Plan in progress',
        3: 'Path in progress',
    };
    return {
        id: 'blueprint',
        title: 'Design My Life Blueprint',
        kind: 'blueprint',
        percent: Math.round((completed / 4) * 100),
        completed,
        total: 4,
        status: completed > 0 ? 'in_progress' : 'not_started',
        detail: labels[completed] || labels[0],
        color: '#5b9fd4',
    };
}

function moduleProgram(id, title, modules, color) {
    const list = Array.isArray(modules) ? modules : [];
    const completed = list.filter((m) => m?.isCompleted).length;
    const total = list.length;
    const percent = calcProgress(list);
    let status = 'not_started';
    if (total === 0) status = 'locked';
    else if (percent >= 100) status = 'completed';
    else if (completed > 0) status = 'in_progress';
    return {
        id,
        title,
        kind: 'core',
        percent,
        completed,
        total,
        status,
        detail:
            total > 0
                ? `${completed} of ${total} modules`
                : 'Program not started yet',
        color,
    };
}

function courseProgram(course) {
    const steps = Array.isArray(course?.steps) ? course.steps : [];
    let completed = steps.filter((s) => s?.completed).length;
    let total = steps.length;
    let percent =
        total > 0 ? Math.round((completed / total) * 100) : 0;
    if (course?.completed) {
        percent = 100;
        if (total === 0) {
            completed = 1;
            total = 1;
        }
    }
    return {
        id: `course-${course.id ?? course.courseId ?? course.title}`,
        title: course.title || course.name || 'Additional program',
        kind: 'additional',
        percent,
        completed,
        total: total || (course.completed ? 1 : 0),
        status: course.completed
            ? 'completed'
            : percent > 0
              ? 'in_progress'
              : 'not_started',
        detail:
            total > 0
                ? `${completed} of ${total} steps`
                : course.completed
                  ? 'Completed'
                  : 'In your library',
        color: '#e8924a',
    };
}

/**
 * Profile V2 — journey + program progress (client-composed from existing APIs).
 * Actions/goals are account-wide; programs use module completion %.
 */
const ProgramsProgressPanel = ({ active = true, progress = null, profile }) => {
    const { checkUserSession } = useContext(AppContext);
    const session = checkUserSession();
    const { token, userId } = session;

    const [loading, setLoading] = useState(true);
    const [todayActions, setTodayActions] = useState(0);
    const [goalsInProgress, setGoalsInProgress] = useState(0);
    const [goalsCompletedList, setGoalsCompletedList] = useState(0);
    const [modules, setModules] = useState(null);
    const [courses, setCourses] = useState([]);

    const userStepId = parseInt(
        session.userStepId || localStorage.getItem('userStepId') || '0',
        10
    );

    useEffect(() => {
        if (!active || !token || !userId) return undefined;
        let cancelled = false;

        (async () => {
            setLoading(true);
            const [actionsRes, goalsRes, modulesRes, coursesRes] =
                await Promise.all([
                    requestAxios({
                        url: `Actions/GetUserNotCompletedActions/${userId}/0`,
                        token,
                    }),
                    requestAxios({
                        url: `goals/GetUserGoalsGroupedByStatus/${userId}`,
                        token,
                    }),
                    loadElearningModules(userId, token),
                    loadElearningCourses(userId, token),
                ]);

            if (cancelled) return;

            if (!actionsRes.error && actionsRes.data) {
                setTodayActions(
                    Number(actionsRes.data.todayActionsCount) || 0
                );
            }
            if (!goalsRes.error && goalsRes.data) {
                setGoalsInProgress(
                    (goalsRes.data.inProgress || []).length
                );
                setGoalsCompletedList(
                    (goalsRes.data.completed || []).length
                );
            }
            if (!modulesRes.error && modulesRes.data) {
                setModules(modulesRes.data);
            }
            if (!coursesRes.error && Array.isArray(coursesRes.data)) {
                setCourses(coursesRes.data);
            }
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [active, token, userId]);

    const lifetimeActions = Number(progress?.stepsTaken) || 0;
    const lifetimeGoals =
        Number(progress?.goalsCompleted) || goalsCompletedList || 0;
    const currentStreak = Number(progress?.currentStreak) || 0;
    const longestStreak = Number(progress?.longestStreak) || 0;

    const todayActionPct = Math.round(
        (Math.min(todayActions, DAILY_ACTION_TARGET) / DAILY_ACTION_TARGET) *
            100
    );
    const goalsActiveTotal = goalsInProgress + lifetimeGoals;
    const goalsLifetimePct =
        goalsActiveTotal > 0
            ? Math.round((lifetimeGoals / goalsActiveTotal) * 100)
            : lifetimeGoals > 0
              ? 100
              : 0;

    const programs = useMemo(() => {
        const rows = [
            blueprintProgress(userStepId),
            moduleProgram(
                PROGRAM_IDS.BREAKTHROUGH,
                PROGRAM_CONFIG[PROGRAM_IDS.BREAKTHROUGH].stripTitle,
                modules?.myBreakthroughExperience,
                '#15c285'
            ),
            moduleProgram(
                PROGRAM_IDS.PATH,
                PROGRAM_CONFIG[PROGRAM_IDS.PATH].stripTitle,
                modules?.myPersonalizedPath,
                '#efd078'
            ),
        ];

        courses.forEach((course) => {
            rows.push(courseProgram(course));
        });

        // Hide empty locked core programs that have no modules yet only if
        // blueprint incomplete and both are empty — still show Blueprint.
        return rows.filter((row) => {
            if (row.kind === 'blueprint') return true;
            if (row.kind === 'additional') return true;
            return row.total > 0 || userStepId >= 4;
        });
    }, [modules, courses, userStepId]);

    const customerLevel = profile?.customerLevel
        ? String(profile.customerLevel)
        : null;

    return (
        <section className="dv2-pp" aria-label="Your progress">
            <header className="dv2-pp__header">
                <div>
                    <h2 className="dv2-pp__title">Your Progress</h2>
                    <p className="dv2-pp__subtitle">
                        Actions and goals across your account, plus completion
                        for each program you have
                        {customerLevel ? ` · ${customerLevel}` : ''}
                    </p>
                </div>
            </header>

            {loading ? (
                <p className="dv2-pp__status">Loading progress…</p>
            ) : (
                <>
                    <div className="dv2-pp__kpis" role="list">
                        <article className="dv2-pp__kpi" role="listitem">
                            <ProgressRing
                                percent={todayActionPct}
                                color="#efd078"
                            />
                            <div className="dv2-pp__kpi-copy">
                                <strong>
                                    {Math.min(todayActions, DAILY_ACTION_TARGET)}
                                    <em> / {DAILY_ACTION_TARGET}</em>
                                </strong>
                                <span>Actions today</span>
                                <small>Daily streak ritual</small>
                            </div>
                        </article>

                        <article className="dv2-pp__kpi" role="listitem">
                            <ProgressRing
                                percent={towardNextMilestone(
                                    lifetimeActions,
                                    ACTION_MILESTONES
                                )}
                                color="#15c285"
                            />
                            <div className="dv2-pp__kpi-copy">
                                <strong>{lifetimeActions}</strong>
                                <span>Actions lifetime</span>
                                <small>Toward next milestone</small>
                            </div>
                        </article>

                        <article className="dv2-pp__kpi" role="listitem">
                            <ProgressRing
                                percent={goalsLifetimePct}
                                color="#5b9fd4"
                            />
                            <div className="dv2-pp__kpi-copy">
                                <strong>
                                    {lifetimeGoals}
                                    {goalsInProgress > 0 ? (
                                        <em> · {goalsInProgress} active</em>
                                    ) : null}
                                </strong>
                                <span>Goals lifetime</span>
                                <small>Completed on your journey</small>
                            </div>
                        </article>

                        <article className="dv2-pp__kpi" role="listitem">
                            <ProgressRing
                                percent={
                                    longestStreak > 0
                                        ? Math.round(
                                              (currentStreak / longestStreak) *
                                                  100
                                          )
                                        : currentStreak > 0
                                          ? 100
                                          : 0
                                }
                                color="#e8924a"
                            />
                            <div className="dv2-pp__kpi-copy">
                                <strong>
                                    {currentStreak}
                                    <em> day</em>
                                </strong>
                                <span>Current streak</span>
                                <small>
                                    Best {longestStreak || currentStreak} days
                                </small>
                            </div>
                        </article>
                    </div>

                    <ProgressActivityChart active={active} />

                    <h3 className="dv2-pp__section-title">Programs</h3>
                    <ul className="dv2-pp__programs">
                        {programs.map((p) => (
                            <li key={p.id}>
                                <article
                                    className={`dv2-pp__program is-${p.status}`}
                                >
                                    <ProgressRing
                                        percent={p.percent}
                                        size={72}
                                        color={p.color}
                                    />
                                    <div className="dv2-pp__program-body">
                                        <div className="dv2-pp__program-top">
                                            <h4>{p.title}</h4>
                                            <span className="dv2-pp__badge">
                                                {p.status === 'completed'
                                                    ? 'Complete'
                                                    : p.status === 'in_progress'
                                                      ? 'In progress'
                                                      : p.status === 'locked'
                                                        ? 'Not started'
                                                        : 'Ready'}
                                            </span>
                                        </div>
                                        <p className="dv2-pp__program-detail">
                                            {p.detail}
                                        </p>
                                        <div
                                            className="dv2-pp__bar"
                                            aria-hidden
                                        >
                                            <i
                                                style={{
                                                    width: `${p.percent}%`,
                                                    background: p.color,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </article>
                            </li>
                        ))}
                    </ul>

                    <p className="dv2-pp__footnote">
                        Actions and goals are account-wide. Program bars reflect
                        module or step completion for each program.
                    </p>
                </>
            )}
        </section>
    );
};

export default ProgramsProgressPanel;
